import { DecimalPipe } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input, signal, type WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { WebsocketsEvents } from '@api/devices';
import { signalHelpers, TimeAgoPipe } from '@core';
import { type DeviceAdditionalAttribute, type DeviceData } from '@devices';
import { SocketEvents } from '@ngrx-store';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { filter, Subscription } from 'rxjs';

import { type WidgetRoomState } from '../../../models';
import { DashboardStore } from '../../../stores';

@Component({
  imports: [
    DecimalPipe,
    MatIcon,
    NgxSkeletonLoaderModule,
    TimeAgoPipe,
  ],
  selector: 'avs-room-state',
  styleUrl: './room-state.component.scss',
  templateUrl: './room-state.component.html',
})
export class RoomStateComponent {
  config = input.required<WidgetRoomState>();

  protected airMonitor = computed(() => {
    const state = this.airMonitorState();

    return state ? { ...state, ...this.airMonitorOverlay() } as DeviceData : null;
  });
  protected camera = computed(() => {
    const state = this.cameraState();

    return state ? { ...state, ...this.cameraOverlay() } as DeviceData : null;
  });
  protected room = computed(() => this.roomState());

  protected people = signal(0);
  protected temperature = signal<number | null>(null);
  protected temperatureUnit = computed(() => this.temperatureChartState()?.measurementUnit ?? null);
  protected humidity = signal<number | null>(null);
  protected co2 = signal<number | null>(null);
  protected pm25 = signal<number | null>(null);
  protected pm10 = signal<number | null>(null);
  protected tvoc = signal<number | null>(null);

  protected isLoading = computed(() =>
    !this.airMonitorState() || !this.cameraState() || !this.roomState() ||
    !this.peopleChartState() || !this.temperatureChartState() || !this.humidityChartState() ||
    !this.co2ChartState() || !this.pm25ChartState() || !this.pm10ChartState() || !this.tvocChartState(),
  );

  private store = inject(DashboardStore);
  private destroyRef = inject(DestroyRef);

  private airMonitorKey = signal('');
  private cameraKey = signal('');
  private roomKey = signal('');
  private peopleKey = signal('');
  private temperatureKey = signal('');
  private humidityKey = signal('');
  private co2Key = signal('');
  private pm25Key = signal('');
  private pm10Key = signal('');
  private tvocKey = signal('');

  private airMonitorState = computed(() => this.store.deviceDetails(this.airMonitorKey())());
  private cameraState = computed(() => this.store.deviceDetails(this.cameraKey())());
  private roomState = computed(() => this.store.spaceInfo(this.roomKey())());
  private peopleChartState = computed(() => this.store.chartData(this.peopleKey())());
  private temperatureChartState = computed(() => this.store.chartData(this.temperatureKey())());
  private humidityChartState = computed(() => this.store.chartData(this.humidityKey())());
  private co2ChartState = computed(() => this.store.chartData(this.co2Key())());
  private pm25ChartState = computed(() => this.store.chartData(this.pm25Key())());
  private pm10ChartState = computed(() => this.store.chartData(this.pm10Key())());
  private tvocChartState = computed(() => this.store.chartData(this.tvocKey())());

  private airMonitorOverlay = signal<Partial<DeviceData>>({});
  private cameraOverlay = signal<Partial<DeviceData>>({});

  private socket$?: Subscription;

  constructor() {
    signalHelpers.untrackedEffect([this.config], (config) => {
      const dps = config.datapoints;

      this.airMonitorKey.set(this.store.loadDeviceDetails(config.devices.airMonitor));
      this.cameraKey.set(this.store.loadDeviceDetails(config.devices.camera));
      this.roomKey.set(this.store.loadSpaceInfo(config.locationId, config.roomId));

      this.peopleKey.set(this.store.loadChartData(config.devices.camera, { latestOnly: true, point: dps.people }));
      this.temperatureKey.set(this.store.loadChartData(config.devices.airMonitor, { latestOnly: true, point: dps.temperature }));
      this.humidityKey.set(this.store.loadChartData(config.devices.airMonitor, { latestOnly: true, point: dps.humidity }));
      this.co2Key.set(this.store.loadChartData(config.devices.airMonitor, { latestOnly: true, point: dps.co2 }));
      this.pm25Key.set(this.store.loadChartData(config.devices.airMonitor, { latestOnly: true, point: dps.pm25 }));
      this.pm10Key.set(this.store.loadChartData(config.devices.airMonitor, { latestOnly: true, point: dps.pm10 }));
      this.tvocKey.set(this.store.loadChartData(config.devices.airMonitor, { latestOnly: true, point: dps.tvoc }));

      this.airMonitorOverlay.set({});
      this.cameraOverlay.set({});

      this.socket$?.unsubscribe();
      this.socket$ = SocketEvents
        .pipe(
          filter(({ data, type }) =>
            type === WebsocketsEvents.DEVICE_UPDATED &&
            (data.id === config.devices.airMonitor || data.id === config.devices.camera),
          ),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(({ data }) => {
          if (data.id === config.devices.airMonitor) {
            this.airMonitorOverlay.update(overlay => ({ ...overlay, ...data }));
          } else {
            this.cameraOverlay.update(overlay => ({ ...overlay, ...data }));
          }

          this.updateDatapoints(data.additionalAttributes as DeviceAdditionalAttribute[]);
        });
    });

    effect(() => {
      this.setIfValid(this.people, this.peopleChartState()?.timeseries[0]?.value);
      this.setIfValid(this.temperature, this.temperatureChartState()?.timeseries[0]?.value);
      this.setIfValid(this.humidity, this.humidityChartState()?.timeseries[0]?.value);
      this.setIfValid(this.co2, this.co2ChartState()?.timeseries[0]?.value);
      this.setIfValid(this.pm25, this.pm25ChartState()?.timeseries[0]?.value);
      this.setIfValid(this.pm10, this.pm10ChartState()?.timeseries[0]?.value);
      this.setIfValid(this.tvoc, this.tvocChartState()?.timeseries[0]?.value);
    });
  }

  private updateDatapoints(additionalAttributes: { name: string; value: string | number | null; }[]) {
    const dps = this.config().datapoints;
    const find = (name: string) => additionalAttributes.find(a => a.name === name);

    this.setIfValid(this.people, find(dps.people)?.value);
    this.setIfValid(this.temperature, find(dps.temperature)?.value);
    this.setIfValid(this.humidity, find(dps.humidity)?.value);
    this.setIfValid(this.co2, find(dps.co2)?.value);
    this.setIfValid(this.pm25, find(dps.pm25)?.value);
    this.setIfValid(this.pm10, find(dps.pm10)?.value);
    this.setIfValid(this.tvoc, find(dps.tvoc)?.value);
  }

  private setIfValid(target: WritableSignal<number | null>, value: string | number | null | undefined) {
    if (value == null) {
      return;
    }

    const num = Number(value);

    if (!isNaN(num)) {
      target.set(num);
    }
  }
}
